import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  SponsorStatisticsFetchSuccessAction,
  StatisticsState,
  StatisticsStateMap,
  StateKey,
  SponsorStatisticsActions,
  FetchActionPayload,
  StatisticsParams,
} from './sponsorStatistics.types';
import { createSliceHook } from 'src/store-redux/helpers';
import { useAppDispatch } from '@hooks/redux';

const createInitialState = <T>(
  defaultData: T | null = null
): StatisticsState<T> => ({
  loading: false,
  error: undefined,
  data: defaultData,
});

const initialState: {
  [K in StateKey]: StatisticsState<StatisticsStateMap[K]>;
} = {
  dailyQC: createInitialState(),
  dailyReads: createInitialState(),
  qcPerStatus: createInitialState(),
  uploadsPerVisit: createInitialState(),
  readingsPerStatus: createInitialState(),
  uploadsPerSite: createInitialState(),
};

const createFetchActions = <K extends StateKey>(key: K) => ({
  [`fetch${key.charAt(0).toUpperCase() + key.slice(1)}`]: {
    reducer: (state: typeof initialState) => {
      state[key].loading = true;
      state[key].error = undefined;
    },
    prepare: (args: { clinicalTrialId: string; params?: StatisticsParams }) => ({
      payload: args,
    }),
  },
  [`fetch${key.charAt(0).toUpperCase() + key.slice(1)}Success`]: (
    state: typeof initialState,
    action: SponsorStatisticsFetchSuccessAction<StatisticsStateMap[K]>
  ) => {
    state[key].data = action.payload;
    state[key].loading = false;
  },
  [`fetch${key.charAt(0).toUpperCase() + key.slice(1)}Error`]: (
    state: typeof initialState,
    action: PayloadAction<string>
  ) => {
    state[key].error = action.payload;
    state[key].loading = false;
  },
});

export const SponsorStatisticsSlice = createSlice({
  name: 'sponsorStatistics',
  initialState,
  reducers: {
    ...createFetchActions('dailyQC'),
    ...createFetchActions('dailyReads'),
    ...createFetchActions('uploadsPerSite'),
    ...createFetchActions('uploadsPerVisit'),
    ...createFetchActions('qcPerStatus'),
    ...createFetchActions('readingsPerStatus'),
    clearAll: state => {
      (Object.keys(state) as StateKey[]).forEach(key => {
        state[key].loading = false;
        state[key].error = undefined;
        state[key].data = null;
      });
    },
  },
});

export default SponsorStatisticsSlice.reducer;

export const allActions =
  SponsorStatisticsSlice.actions as SponsorStatisticsActions;

export const useSponsorStatisticsSlice = createSliceHook(
  SponsorStatisticsSlice.name,
  SponsorStatisticsSlice.actions
);

export const useSponsorStatistics = () => {
  const state = useSponsorStatisticsSlice();
  const dispatch = useAppDispatch();

  return {
    state,
    actions: {
      fetchDailyQC: (payload: FetchActionPayload) =>
        dispatch(allActions.fetchDailyQC(payload)),
      fetchDailyReads: (payload: FetchActionPayload) =>
        dispatch(allActions.fetchDailyReads(payload)),
      fetchUploadsPerSite: (payload: FetchActionPayload) =>
        dispatch(allActions.fetchUploadsPerSite(payload)),
      fetchUploadsPerVisit: (payload: FetchActionPayload) =>
        dispatch(allActions.fetchUploadsPerVisit(payload)),
      fetchQcPerStatus: (payload: FetchActionPayload) =>
        dispatch(allActions.fetchQcPerStatus(payload)),
      fetchReadingsPerStatus: (payload: FetchActionPayload) =>
        dispatch(allActions.fetchReadingsPerStatus(payload)),
    },
  };
};
